/** Every supported tenant theme color has an explicit CSS consumer. Unknown keys are rejected. */
const token = (cssVariable: string, defaultValue: `#${string}`, group: string, label: string) => ({ cssVariable, defaultValue, group, label });

export const STUDENT_THEME_COLOR_TOKENS = {
  primary: token('--primary', '#123F43', 'Buttons', 'Primary background'),
  primaryForeground: token('--primary-foreground', '#FFFFFF', 'Buttons', 'Primary text'),
  primaryHover: token('--primary-hover', '#0E3438', 'Buttons', 'Primary hover'),
  primaryActive: token('--primary-active', '#09262A', 'Buttons', 'Primary pressed'),
  secondary: token('--secondary', '#E5EFED', 'Buttons', 'Secondary background'),
  secondaryForeground: token('--secondary-foreground', '#123F43', 'Buttons', 'Secondary text'),
  secondaryHover: token('--secondary-hover', '#D4E4E1', 'Buttons', 'Secondary hover'),
  accent: token('--accent', '#E5EFED', 'Buttons', 'Selection background'),
  accentForeground: token('--accent-foreground', '#123F43', 'Buttons', 'Selection text'),
  highlight: token('--gold', '#C89743', 'Buttons', 'Highlight background'),
  highlightForeground: token('--gold-foreground', '#09262A', 'Buttons', 'Highlight text'),
  highlightHover: token('--gold-hover', '#B68736', 'Buttons', 'Highlight hover'),
  background: token('--background', '#FAF8F2', 'Surfaces', 'Page background'),
  foreground: token('--foreground', '#203335', 'Surfaces', 'Page text'),
  card: token('--card', '#FFFFFF', 'Surfaces', 'Card background'),
  cardForeground: token('--card-foreground', '#203335', 'Surfaces', 'Card text'),
  popover: token('--popover', '#FFFFFF', 'Surfaces', 'Popover background'),
  popoverForeground: token('--popover-foreground', '#203335', 'Surfaces', 'Popover text'),
  muted: token('--muted', '#F0F2ED', 'Surfaces', 'Muted background'),
  mutedForeground: token('--muted-foreground', '#596968', 'Surfaces', 'Muted text'),
  border: token('--border', '#DEE4DF', 'Surfaces', 'Border'),
  input: token('--input', '#7A8A89', 'Surfaces', 'Input border'),
  ring: token('--ring', '#123F43', 'Surfaces', 'Focus ring'),
  link: token('--link', '#123F43', 'Surfaces', 'Link and eyebrow text'),
  linkHover: token('--link-hover', '#0E3438', 'Surfaces', 'Link hover'),
  shadow: token('--theme-shadow', '#123F43', 'Surfaces', 'Shadow color'),
  sidebarBackground: token('--sidebar-background', '#FFFFFF', 'Navigation', 'Sidebar background'),
  sidebarForeground: token('--sidebar-foreground', '#203335', 'Navigation', 'Sidebar text'),
  headerBackground: token('--header-background', '#FFFFFF', 'Navigation', 'Header background'),
  headerForeground: token('--header-foreground', '#203335', 'Navigation', 'Header text'),
  navigationText: token('--navigation-text', '#596968', 'Navigation', 'Navigation text'),
  navigationActive: token('--navigation-active', '#E5EFED', 'Navigation', 'Active navigation background'),
  navigationActiveForeground: token('--navigation-active-foreground', '#123F43', 'Navigation', 'Active navigation text'),
  navigationHover: token('--navigation-hover', '#E5EFED', 'Navigation', 'Navigation hover background'),
  navigationHoverForeground: token('--navigation-hover-foreground', '#123F43', 'Navigation', 'Navigation hover text'),
  workspaceBackground: token('--workspace-background', '#F0F2ED', 'Navigation', 'Welcome / workspace background'),
  workspaceForeground: token('--workspace-foreground', '#203335', 'Navigation', 'Welcome / workspace text'),
  progressTrack: token('--progress-track', '#F0F2ED', 'Navigation', 'Progress track'),
  progressFill: token('--progress-fill', '#15803D', 'Navigation', 'Progress fill'),
  success: token('--success', '#15803D', 'Status', 'Success'),
  successForeground: token('--success-foreground', '#FFFFFF', 'Status', 'Success text'),
  successSoft: token('--success-soft', '#F0FDF4', 'Status', 'Success soft background'),
  successSoftForeground: token('--success-soft-foreground', '#166534', 'Status', 'Success soft text'),
  warning: token('--warning', '#B45309', 'Status', 'Warning'),
  warningForeground: token('--warning-foreground', '#FFFFFF', 'Status', 'Warning text'),
  warningSoft: token('--warning-soft', '#FFFBEB', 'Status', 'Warning soft background'),
  warningSoftForeground: token('--warning-soft-foreground', '#92400E', 'Status', 'Warning soft text'),
  error: token('--destructive', '#B91C1C', 'Status', 'Error'),
  errorForeground: token('--destructive-foreground', '#FFFFFF', 'Status', 'Error text'),
  errorSoft: token('--destructive-soft', '#FEF2F2', 'Status', 'Error soft background'),
  errorSoftForeground: token('--destructive-soft-foreground', '#991B1B', 'Status', 'Error soft text'),
  info: token('--info', '#1D4ED8', 'Status', 'Information'),
  infoForeground: token('--info-foreground', '#FFFFFF', 'Status', 'Information text'),
  infoSoft: token('--info-soft', '#EFF6FF', 'Status', 'Information soft background'),
  infoSoftForeground: token('--info-soft-foreground', '#1E40AF', 'Status', 'Information soft text'),
} as const;

export type StudentThemeColorKey = keyof typeof STUDENT_THEME_COLOR_TOKENS;
export interface StudentThemeConfig {
  schemaVersion: 1;
  colors: Record<StudentThemeColorKey, `#${string}`>;
  shape: { controlRadius: number; cardRadius: number; dialogRadius: number };
  typography: { bodyFont: 'inter' | 'system'; headingFont: 'jakarta' | 'inter' | 'system' };
}

export const DEFAULT_STUDENT_THEME: StudentThemeConfig = {
  schemaVersion: 1,
  colors: Object.fromEntries(Object.entries(STUDENT_THEME_COLOR_TOKENS).map(([key, value]) => [key, value.defaultValue])) as StudentThemeConfig['colors'],
  shape: { controlRadius: 8, cardRadius: 12, dialogRadius: 16 },
  typography: { bodyFont: 'inter', headingFont: 'jakarta' },
};

const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
export function parseStudentThemeConfig(value: unknown): { success: true; data: StudentThemeConfig } | { success: false; issues: string[] } {
  const issues: string[] = [];
  if (!object(value)) return { success: false, issues: ['Theme must be a JSON object.'] };
  for (const key of Object.keys(value)) if (!['schemaVersion', 'colors', 'shape', 'typography'].includes(key)) issues.push(`Unsupported theme field: ${key}.`);
  if (value.schemaVersion !== 1) issues.push('schemaVersion must be 1.');
  const colors = { ...DEFAULT_STUDENT_THEME.colors };
  if (!object(value.colors)) issues.push('colors must be an object.');
  else for (const [key, color] of Object.entries(value.colors)) {
    if (!Object.hasOwn(STUDENT_THEME_COLOR_TOKENS, key)) issues.push(`Unsupported color: ${key}.`);
    else if (typeof color !== 'string' || !/^#[\da-f]{6}$/i.test(color)) issues.push(`colors.${key} must be a six-digit hex color.`);
    else colors[key as StudentThemeColorKey] = color.toUpperCase() as `#${string}`;
  }
  const shape = { ...DEFAULT_STUDENT_THEME.shape };
  if (value.shape !== undefined) {
    if (!object(value.shape)) issues.push('shape must be an object.');
    else for (const [key, radius] of Object.entries(value.shape)) {
      if (!Object.hasOwn(shape, key)) issues.push(`Unsupported shape field: ${key}.`);
      else if (typeof radius !== 'number' || !Number.isFinite(radius) || radius < 0 || radius > 40) issues.push(`shape.${key} must be between 0 and 40 pixels.`);
      else shape[key as keyof typeof shape] = radius;
    }
  }
  const typography = { ...DEFAULT_STUDENT_THEME.typography };
  if (value.typography !== undefined) {
    if (!object(value.typography)) issues.push('typography must be an object.');
    else for (const [key, font] of Object.entries(value.typography)) {
      if (key === 'bodyFont' && (font === 'inter' || font === 'system')) typography.bodyFont = font;
      else if (key === 'headingFont' && (font === 'jakarta' || font === 'inter' || font === 'system')) typography.headingFont = font;
      else issues.push(`Unsupported typography setting: ${key}.`);
    }
  }
  return issues.length ? { success: false, issues } : { success: true, data: { schemaVersion: 1, colors, shape, typography } };
}

export function studentThemeContrastRatio(first: string, second: string): number {
  const luminance = (hex: string) => {
    const [r = 0, g = 0, b = 0] = [1, 3, 5].map(offset => {
      const c = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
      return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const a = luminance(first), b = luminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** Drafts may be incomplete visually; publishing checks readable foreground/background pairs. */
export function getStudentThemeContrastIssues(theme: StudentThemeConfig): string[] {
  const pairs: [StudentThemeColorKey, StudentThemeColorKey][] = [
    ['primaryForeground', 'primary'], ['primaryForeground', 'primaryHover'], ['primaryForeground', 'primaryActive'],
    ['secondaryForeground', 'secondary'], ['secondaryForeground', 'secondaryHover'], ['accentForeground', 'accent'],
    ['highlightForeground', 'highlight'], ['highlightForeground', 'highlightHover'], ['foreground', 'background'],
    ['cardForeground', 'card'], ['popoverForeground', 'popover'], ['mutedForeground', 'muted'], ['mutedForeground', 'background'], ['mutedForeground', 'card'],
    ['link', 'background'], ['link', 'card'], ['linkHover', 'background'], ['linkHover', 'card'], ['sidebarForeground', 'sidebarBackground'],
    ['headerForeground', 'headerBackground'], ['navigationText', 'sidebarBackground'], ['navigationActiveForeground', 'navigationActive'],
    ['navigationHoverForeground', 'navigationHover'], ['workspaceForeground', 'workspaceBackground'],
    ['successForeground', 'success'], ['successSoftForeground', 'successSoft'], ['warningForeground', 'warning'],
    ['warningSoftForeground', 'warningSoft'], ['errorForeground', 'error'], ['errorSoftForeground', 'errorSoft'],
    ['infoForeground', 'info'], ['infoSoftForeground', 'infoSoft'],
  ];
  return pairs.flatMap(([foreground, background]) => {
    const ratio = studentThemeContrastRatio(theme.colors[foreground], theme.colors[background]);
    return ratio < 4.5 ? [`${foreground} on ${background}: ${ratio.toFixed(2)}:1 (minimum 4.5:1).`] : [];
  });
}
